import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import '../../css/frontend/resources-page.css';

import membershipIcon from '../../assets/team.png';
import labIcon from '../../assets/blueprint.png';

const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

export default function ResourcesPage() {
    const { t } = useTranslation();
    const [activeGroup, setActiveGroup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [resources, setResources] = useState([]);
    const [categories, setCategories] = useState([]);

    const formatDateTime = (value) => {
        if (!value) return '—';

        try {
            return new Date(value).toLocaleString();
        } catch {
            return value;
        }
    };

    const formatLanguageLabel = (lang) => {
        if (!lang) return '—';

        const normalized = String(lang).toUpperCase().replace(/\s+/g, '');

        if (normalized.includes('EL') && normalized.includes('EN')) {
            return 'Ελληνικά / English';
        }

        if (normalized.includes('EL')) {
            return 'Ελληνικά';
        }

        if (normalized.includes('EN')) {
            return 'English';
        }

        return lang;
    };

    const loadResources = async () => {
        try {
            setLoading(true);
            setError('');

            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const token = userInfo?.token;

            const response = await fetch(`${apiUrl}/api/resources`, {
                method: 'GET',
                headers: token
                    ? {
                        Authorization: `Bearer ${token}`,
                    }
                    : {},
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.error || 'Failed to load resources');
            }

            setResources(data?.data || []);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to load resources');
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await fetch(`${apiUrl}/api/resource-categories`);
            const data = await response.json();

            if (response.ok) {
                setCategories(data?.data || []);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        loadResources();
        loadCategories();
    }, []);

    const categoryByKey = useMemo(() => {
        return new Map(categories.map((c) => [c.key, c]));
    }, [categories]);

    const groups = useMemo(() => {
        const allGroups = Array.from(new Set(resources.map((r) => r.group).filter(Boolean)));

        // A group with a category record that's explicitly inactive is hidden
        // here; a group with no category record yet keeps showing (old
        // hardcoded fallback) until one is created for it.
        return allGroups.filter((group) => {
            const category = categoryByKey.get(group);
            return !category || category.isActive;
        });
    }, [resources, categoryByKey]);

    const visibleResources = activeGroup
        ? resources.filter((r) => r.group === activeGroup)
        : [];

    const labelForGroup = (group) => {
        const category = categoryByKey.get(group);
        if (category) {
            return category.name;
        }
        if (group === 'membership') {
            return t('RES_GROUP_MEMBERSHIP') || 'Membership & Rules';
        }
        if (group === 'lab') {
            return t('RES_GROUP_LAB') || 'Lab & Space';
        }
        return t(`RES_GROUP_${String(group).toUpperCase()}`) || group;
    };

    const iconForGroup = (group) => {
        const category = categoryByKey.get(group);
        if (category?.iconUrl) {
            return `${apiUrl}${category.iconUrl}`;
        }
        if (group === 'membership') return membershipIcon;
        if (group === 'lab') return labIcon;
        return membershipIcon;
    };

    const handleDownloadResource = async (resource) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const token = userInfo?.token;

            const response = await fetch(`${apiUrl}${resource.href}`, {
                method: 'GET',
                headers: token
                    ? {
                        Authorization: `Bearer ${token}`,
                    }
                    : {},
            });

            if (!response.ok) {
                const data = await response.json().catch(() => null);
                throw new Error(data?.error || 'Failed to download resource');
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = resource.fileName || resource.key || 'resource';
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(blobUrl);

            await loadResources();
        } catch (error) {
            console.error(error);
            alert(error.message || 'Failed to download resource');
        }
    };

    return (
        <>
            <Helmet>
                <title>{t('RESOURCES_TITLE_PAGE') || 'SpaceTime Community | Resources'}</title>
                <meta
                    name="description"
                    content={
                        t('RESOURCES_META_DESC') ||
                        'Έγγραφα, κανονισμοί, manuals και floorplans για τα μέλη του SpaceTime.'
                    }
                />
                <meta property="og:title" content="SpaceTime | Resources" />
                <meta property="og:url" content="https://spacetime.community/resources" />
            </Helmet>

            <div className="lab-page resources-page">
                <header className="lab-map resources-hero">
                    <div className="resources-hero-icon" aria-hidden="true">
                        📚
                    </div>
                    <h1 className="lab-title">
                        {t('RESOURCES_TITLE') || 'Resources & Documents'}
                    </h1>
                    <p className="lab-subtitle">
                        {t('RESOURCES_SUBTITLE') ||
                            'Όλα τα βασικά έγγραφα, κανονισμοί και manuals για τα μέλη σε ένα σημείο.'}
                    </p>
                </header>

                <main className="resources-body-container">
                    {loading && <div>{t('Loading resources...')}</div>}
                    {error && <div>{error}</div>}

                    {!loading && !error && !activeGroup && (
                        <section className="resources-categories">
                            {groups.map((group) => (
                                <button
                                    key={group}
                                    type="button"
                                    className="resources-category-btn"
                                    onClick={() => setActiveGroup(group)}
                                >
                                    <img
                                        src={iconForGroup(group)}
                                        alt=""
                                        className="resources-category-icon"
                                    />
                                    <div className="resources-category-title">
                                        {labelForGroup(group)}
                                    </div>
                                </button>
                            ))}
                        </section>
                    )}

                    {!loading && !error && activeGroup && (
                        <>
                            <section className="resources-selected-header">
                                <button
                                    type="button"
                                    className="resources-back-btn"
                                    onClick={() => setActiveGroup(null)}
                                >
                                    ← {t('RES_BACK_TO_MAIN') || 'Back to categories'}
                                </button>
                                <h2 className="resources-group-title">
                                    {labelForGroup(activeGroup)}
                                </h2>
                            </section>

                            <section
                                className="resources-list"
                                aria-label={t('RESOURCES_LIST_LABEL') || 'Λίστα εγγράφων'}
                            >
                                {visibleResources.map((res) => (
                                    <article
                                        key={res.key}
                                        className={`resource-item ${res.isUnread ? 'resource-item--unread' : ''}`}
                                    >
                                        <div className="resource-side">
                                            <div className="resource-icon" aria-hidden="true">
                                                <svg
                                                    className="resource-icon-svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M14 3V8H19"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M9 12H15"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                        strokeLinecap="round"
                                                    />
                                                    <path
                                                        d="M9 16H15"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </div>

                                            <div className="resource-version-below">
                                                v{res.version}
                                            </div>
                                        </div>

                                        <div className={`resource-content ${res.isUnread ? 'resource-content--unread' : ''}`}>
                                            <div className="resource-top-row">
                                                <div className="resource-title-wrap">
                                                    <h3 className="resource-title">{res.title}</h3>

                                                    <div className="resource-status-row">
                        <span className="resource-status-badge">
                            {formatLanguageLabel(res.lang)}
                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {res.description && (
                                                <p className="resource-description">
                                                    {res.description}
                                                </p>
                                            )}

                                            <div className="resource-meta-row">
                                                <div className="resource-meta-left">
                                                    {res.tags?.length > 0 && (
                                                        <ul className="resource-tags">
                                                            {res.tags.map((tag) => (
                                                                <li key={tag} className="resource-tag">
                                                                    {tag}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}

                                                    <div className="resource-last-updated">
                                                        {t('Last updated') || 'Last updated'}:{' '}
                                                        {formatDateTime(res.lastUpdated)}
                                                    </div>

                                                    {/*{res.lastDownloadedAt && (*/}
                                                    {/*    <div className="resource-last-opened">*/}
                                                    {/*        {t('Last opened') || 'Last opened'}:{' '}*/}
                                                    {/*        {formatDateTime(res.lastDownloadedAt)}*/}
                                                    {/*    </div>*/}
                                                    {/*)}*/}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="resource-middle-status">
                                            {res.isUnread && (
                                            <span className="resource-status-badge unread resource-status-badge--floating">
                                                {t('New / unread') || 'New / unread'}
                                            </span>
                                            )}
                                        </div>

                                        <div className="resource-action-side">
                                            <button
                                                type="button"
                                                className="resource-link"
                                                onClick={() => handleDownloadResource(res)}
                                            >
                                                {t('OPEN') || 'OPEN'}
                                            </button>
                                        </div>
                                    </article>
                                ))}
                            </section>
                        </>
                    )}

                    {activeGroup === 'lab' && (
                        <section
                            className="resources-quiet-hours"
                            aria-label={t('RES_QUIET_HOURS_LABEL') || 'Ώρες κοινής ησυχίας'}
                        >
                            <h2 className="resources-quiet-hours-title">
                                {t('RES_QUIET_HOURS_TITLE') || 'Ώρες κοινής ησυχίας'}
                            </h2>

                            <div className="resources-quiet-hours-grid">
                                <article className="quiet-hours-card summer-block">
                                    <h3 className="quiet-hours-period">
                                        {t('RES_QUIET_HOURS_SUMMER_TITLE') || 'Θερινή περίοδος'}
                                    </h3>
                                    <p className="quiet-hours-dates">
                                        {t('RES_QUIET_HOURS_SUMMER_DATES') || 'Από 01/04 έως 30/09'}
                                    </p>
                                    <ul className="quiet-hours-times">
                                        <li>15:00 – 17:30</li>
                                        <li>22:00 – 07:00</li>
                                    </ul>
                                </article>

                                <article className="quiet-hours-card winter-block">
                                    <h3 className="quiet-hours-period">
                                        {t('RES_QUIET_HOURS_WINTER_TITLE') || 'Χειμερινή περίοδος'}
                                    </h3>
                                    <p className="quiet-hours-dates">
                                        {t('RES_QUIET_HOURS_WINTER_DATES') || 'Από 01/10 έως 31/03'}
                                    </p>
                                    <ul className="quiet-hours-times">
                                        <li>15:00 – 17:30</li>
                                        <li>21:30 – 07:30</li>
                                    </ul>
                                </article>
                            </div>
                        </section>
                    )}
                </main>
            </div>
        </>
    );
}